import React, { useState, useEffect, useRef, useCallback } from 'react';
import { notifyError } from '../../../../../utils/bugsnag/notifyError';
import {
  Box,
  Typography,
  Pagination,
  Chip,
  Tooltip,
  Button,
  InputBase,
  IconButton,
  CircularProgress,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { format, fromUnixTime } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import PropTypes from 'prop-types';
import { useStyles as useCommonStyles } from '../ReportSubItems.styles';
import { useStyles } from './DepositReconciliation.styles';
import DropdownFilter from '../../../../input/DropdownFilter/DropdownFilter';
import {
  getStripeReconciliationByPayout,
  STRIPE_RECON_MODE_FALLBACK,
} from '../../../../../services/ReportService';
import { useMenuContext } from '../../../../../context/menuContext';
import { formatCurrencyValue } from '../../../../../utils/FormatUtils/formatCurrencyValue';
import { Loading } from '../../../../layout/Loading';
import { ReportUnavailable } from '../../../../layout/ReportUnavailable';
import { DatePicker } from '../../../../input/DatePicker/DatePicker';
import {
  last7Days,
  lastWeek,
  last30Days,
  lastMonth,
  last3Months,
} from '../../../../../utils/date-utils';

const ESTIMATED_TOOLTIP =
  'Stripe fee estimated as $0.30 + 2.9% of subtotal. Will update once Stripe confirms.';

const UNRECONCILED_TOOLTIP =
  "These four numbers don't reconcile against each other — please contact support.";

// Task #322 reconciliation status. Driven entirely by the signed
// `delta_vs_summed_deposit` the backend now ships next to each
// stripe_payout_amount. Positive = Stripe paid more than our rows summed
// (refunds reversed, late attributions). Negative = our rows summed more
// than Stripe paid (refunds, adjustments, NULL-payout orders).
//
//   |delta| ≤ $0.01    → 'reconciled'           (green check)
//   |delta| ≤ $0.50    → 'variance_minor'       (amber)
//   |delta|  > $0.50   → 'variance_major'       (red)
//   stripePayoutAmount === null → 'awaiting'    (neutral)
function getReconciliationStatus({ stripePayoutAmount, deltaVsSummedDeposit }) {
  if (stripePayoutAmount == null) return 'awaiting';
  const delta = Math.abs(deltaVsSummedDeposit ?? 0);
  if (delta <= 0.01) return 'reconciled';
  if (delta <= 0.5) return 'variance_minor';
  return 'variance_major';
}

function buildVarianceTooltip({ summedDeposit, stripePayoutAmount, deltaVsSummedDeposit }) {
  const drift = Math.abs(deltaVsSummedDeposit ?? 0);
  const direction =
    (deltaVsSummedDeposit ?? 0) > 0
      ? 'Stripe paid more than the order rows added up to'
      : 'the order rows added up to more than Stripe paid';
  return (
    `Our orders summed to ${formatCurrencyValue(summedDeposit)} but Stripe ` +
    `deposited ${formatCurrencyValue(stripePayoutAmount)} — a ` +
    `${formatCurrencyValue(drift)} difference because ${direction}. ` +
    `Common causes: refunds, application-fee refunds, manual top-ups, ` +
    `adjustments, or orders not yet attributed to this payout. Please ` +
    `contact support if you don't recognize the difference.`
  );
}

const AWAITING_STRIPE_TOOLTIP =
  "Stripe hasn't confirmed the payout amount for this deposit yet. Once " +
  'the payout dump syncs, the matched amount and reconciliation status ' +
  'will appear here.';

function DepositReconciliationBadge({
  status,
  summedDeposit,
  stripePayoutAmount,
  deltaVsSummedDeposit,
}) {
  const classes = useStyles();
  if (status === 'awaiting') {
    return (
      <Tooltip title={AWAITING_STRIPE_TOOLTIP} arrow placement="top">
        <Chip
          size="small"
          label="Awaiting Stripe data"
          className={classes.badgeAwaiting}
          data-testid="deposit-awaiting-stripe-badge"
          aria-label="Awaiting Stripe payout data"
        />
      </Tooltip>
    );
  }
  if (status === 'reconciled') {
    return (
      <Chip
        size="small"
        icon={<CheckCircleIcon />}
        label="Reconciled"
        className={classes.badgeReconciled}
        data-testid="deposit-reconciled-badge"
        aria-label="Deposit reconciled to Stripe payout"
      />
    );
  }
  // Variance — minor (amber) or major (red).
  const tooltip = buildVarianceTooltip({
    summedDeposit,
    stripePayoutAmount,
    deltaVsSummedDeposit,
  });
  const isMajor = status === 'variance_major';
  return (
    <Tooltip title={tooltip} arrow placement="top">
      <Chip
        size="small"
        icon={<WarningAmberIcon />}
        label={isMajor ? 'Variance' : 'Variance · minor'}
        className={isMajor ? classes.badgeVarianceMajor : classes.badgeVarianceMinor}
        // Retain the legacy test-id from Task #96 so existing assertions
        // and any saved Bugsnag selectors continue to resolve.
        data-testid="deposit-drift-warning"
        aria-label="Stripe deposit does not match the order rows"
      />
    </Tooltip>
  );
}

DepositReconciliationBadge.propTypes = {
  status: PropTypes.oneOf([
    'reconciled',
    'variance_minor',
    'variance_major',
    'awaiting',
  ]).isRequired,
  summedDeposit: PropTypes.number,
  stripePayoutAmount: PropTypes.number,
  deltaVsSummedDeposit: PropTypes.number,
};

DepositReconciliationBadge.defaultProps = {
  summedDeposit: 0,
  stripePayoutAmount: null,
  deltaVsSummedDeposit: null,
};

// The Stripe Fee on this page is derived as Menu Price − Eatzy Amount −
// Deposit, so it should never be negative when the backend sends consistent
// data. A meaningfully-negative result means the four numbers don't add up
// (e.g., Deposit is missing the sales-tax pass-through, Menu Price is
// stale). The half-cent epsilon swallows harmless floating-point noise so
// we don't flag $-0.0000001 rounding artefacts.
function isFeeUnreconciled(stripeFee) {
  return typeof stripeFee === 'number' && stripeFee < -0.005;
}

function UnreconciledFeeIndicator({ variant }) {
  const classes = useStyles();
  const iconClass =
    variant === 'summary' ? classes.summaryUnreconciledIcon : classes.unreconciledIcon;
  return (
    <Tooltip title={UNRECONCILED_TOOLTIP} arrow placement="top">
      <WarningAmberIcon
        className={iconClass}
        fontSize="small"
        data-testid="stripe-fee-unreconciled-warning"
        aria-label="Stripe fee does not reconcile"
      />
    </Tooltip>
  );
}

UnreconciledFeeIndicator.propTypes = {
  variant: PropTypes.oneOf(['row', 'summary']),
};

UnreconciledFeeIndicator.defaultProps = {
  variant: 'row',
};

function formatPercent(value, { menuPrice } = {}) {
  if (menuPrice === 0) return '—';
  if (value == null || Number.isNaN(value)) return '—';
  return `${(value * 100).toFixed(1)}%`;
}

function formatInRestaurantTz(unixTimestamp, formatStr, timezone) {
  if (!unixTimestamp) return null;
  try {
    const date = fromUnixTime(unixTimestamp);
    const zoned = timezone ? utcToZonedTime(date, timezone) : date;
    return format(zoned, formatStr);
  } catch {
    return null;
  }
}

// Stripe's arrival_date and available_on are UTC-midnight calendar dates.
// They must be rendered in UTC regardless of the browser or restaurant
// timezone — any timezone behind UTC would otherwise shift them one day
// early (e.g. midnight UTC on May 1 becomes 8 pm EDT on April 30).
function formatStripeDateUTC(unixTimestamp, formatStr) {
  if (!unixTimestamp) return null;
  try {
    return format(utcToZonedTime(fromUnixTime(unixTimestamp), 'UTC'), formatStr);
  } catch {
    return null;
  }
}

function formatArrivalDate(unixTimestamp) {
  return formatStripeDateUTC(unixTimestamp, 'MMMM d, yyyy');
}

function formatExpectedHeaderDate(unixTimestamp) {
  return formatStripeDateUTC(unixTimestamp, 'EEEE, MMMM d, yyyy');
}

function formatExpectedChipDate(unixTimestamp) {
  return formatStripeDateUTC(unixTimestamp, 'MMMM d');
}

export function SummaryBar({
  totalMenuPrice,
  totalEatzyAmount,
  totalEffectivePct,
  totalStripeFee,
  totalDeposit,
  stripePayoutTotal,
  stripePayoutTotalAvailable,
}) {
  const classes = useStyles();
  return (
    <Box className={classes.summaryBar}>
      <Box className={classes.summaryCard}>
        <Typography className={classes.summaryLabel}>Menu Price</Typography>
        <Typography className={classes.summaryValue}>
          {formatCurrencyValue(totalMenuPrice)}
        </Typography>
      </Box>
      <Box className={classes.summaryCard}>
        <Typography className={classes.summaryLabel}>Eatzy Amount</Typography>
        <Typography className={`${classes.summaryValue} ${classes.feeValue}`}>
          {formatCurrencyValue(totalEatzyAmount)}
        </Typography>
        <Typography className={classes.summaryHint}>
          Effective {formatPercent(totalEffectivePct, { menuPrice: totalMenuPrice })}
        </Typography>
      </Box>
      <Box className={classes.summaryCard} data-testid="stripe-fee-card">
        <Typography className={classes.summaryLabel}>Stripe Fee</Typography>
        <Box className={classes.summaryValueRow}>
          {isFeeUnreconciled(totalStripeFee) && (
            <UnreconciledFeeIndicator variant="summary" />
          )}
          <Typography className={`${classes.summaryValue} ${classes.feeValue}`}>
            {formatCurrencyValue(totalStripeFee)}
          </Typography>
        </Box>
      </Box>
      <Box
        className={`${classes.summaryCard} ${classes.depositCard}`}
        data-testid="deposit-amount-card"
      >
        <Typography className={classes.summaryLabel}>Deposit Amount</Typography>
        <Typography
          className={`${classes.summaryValue} ${classes.depositValue}`}
          data-testid="summary-deposit-summed"
        >
          {formatCurrencyValue(totalDeposit)}
        </Typography>
        <Typography className={classes.summaryHint}>
          {stripePayoutTotalAvailable ? (
            <>
              Stripe payout total{' '}
              <span data-testid="summary-stripe-payout-total">
                {formatCurrencyValue(stripePayoutTotal)}
              </span>
            </>
          ) : (
            <span data-testid="summary-stripe-payout-pending">
              Stripe payout total — awaiting Stripe data
            </span>
          )}
        </Typography>
      </Box>
    </Box>
  );
}

SummaryBar.propTypes = {
  totalMenuPrice: PropTypes.number,
  totalEatzyAmount: PropTypes.number,
  totalEffectivePct: PropTypes.number,
  totalStripeFee: PropTypes.number,
  totalDeposit: PropTypes.number,
  stripePayoutTotal: PropTypes.number,
  stripePayoutTotalAvailable: PropTypes.bool,
};

SummaryBar.defaultProps = {
  stripePayoutTotal: 0,
  stripePayoutTotalAvailable: false,
};

export function FallbackBanner({ onRetry }) {
  const classes = useStyles();
  return (
    <Box
      className={classes.fallbackBanner}
      data-testid="stripe-recon-fallback-banner"
      role="status"
    >
      <Box className={classes.fallbackBannerText}>
        <Typography className={classes.fallbackBannerTitle}>
          Showing transactions by order date
        </Typography>
        <Typography className={classes.fallbackBannerBody}>
          The deposit-arrival view is temporarily unavailable, so we&apos;re
          listing orders by transaction date instead. Totals on this page may
          not match an individual Stripe payout.
        </Typography>
      </Box>
      {onRetry && (
        <Button
          variant="text"
          size="small"
          onClick={onRetry}
          className={classes.fallbackBannerRetry}
          data-testid="stripe-recon-fallback-retry"
        >
          Try again
        </Button>
      )}
    </Box>
  );
}

FallbackBanner.propTypes = {
  onRetry: PropTypes.func,
};

FallbackBanner.defaultProps = {
  onRetry: undefined,
};

export function ColumnHeaders({ titleStyle }) {
  const classes = useStyles();
  return (
    <Box className={titleStyle}>
      <Typography className={`${classes.columnTitle} ${classes.colDateTime}`}>
        ORDER DATE
      </Typography>
      <Typography className={`${classes.columnTitle} ${classes.colType}`}>
        TYPE
      </Typography>
      <Typography className={`${classes.columnTitle} ${classes.colId}`}>
        ORDER ID
      </Typography>
      <Typography
        className={`${classes.columnTitle} ${classes.colMenuPrice}`}
      >
        MENU PRICE
      </Typography>
      <Typography
        className={`${classes.columnTitle} ${classes.colEatzyAmount}`}
      >
        EATZY AMOUNT
      </Typography>
      <Typography
        className={`${classes.columnTitle} ${classes.colEffectivePct}`}
      >
        EFFECTIVE %
      </Typography>
      <Typography className={`${classes.columnTitle} ${classes.colStripeFee}`}>
        STRIPE FEE
      </Typography>
      <Typography className={`${classes.columnTitle} ${classes.colDeposit}`}>
        DEPOSIT
      </Typography>
      <Typography className={`${classes.columnTitle} ${classes.colStatus}`}>
        STATUS
      </Typography>
    </Box>
  );
}

ColumnHeaders.propTypes = {
  titleStyle: PropTypes.string,
};

// Exported so it can be unit-tested directly without triggering the MUI
// Tooltip portal.
export function RowBreakdownTooltip({ row }) {
  const classes = useStyles();

  // Use retail_price (quantity-weighted, line-item-level retail value) when the
  // API provides it. Fall back to deriving the food total from the transaction
  // residual for older API responses that predate the retail_price field.
  const derivedFoodTotal =
    (row.transaction ?? 0) -
    (row.salesTax ?? 0) -
    (row.deliveryFee ?? 0) -
    (row.tip ?? 0) -
    (row.serviceFee ?? 0) -
    (row.adjustments ?? 0) -
    (row.driverFees ?? 0) -
    (row.restoDeliveryFee ?? 0);

  const retailPrice = row.retailPrice ?? 0;
  const foodTotal = retailPrice !== 0 ? retailPrice : derivedFoodTotal;
  const foodTotalLabel = 'Food Total';

  // Residual: absorbs delivery fee, tip, service fee, driver fees, adjustments, etc.
  // Guarantees top section sums to transaction for any food-total path.
  const tipsAndFees = (row.transaction ?? 0) - foodTotal - (row.salesTax ?? 0);

  // Amounts remitted to third parties — not Eatzy revenue, despite Stripe grouping them.
  const passThroughs = [
    ['Sales tax (remitted)', row.salesTax],
    ['Driver fees (to driver)', row.driverFees],
    ['Resto delivery fee', row.restoDeliveryFee],
  ].filter(([, v]) => v != null && v !== 0);

  return (
    <Box className={classes.tooltipBody}>

      {/* TOP: What the customer paid */}
      <Box className={classes.tooltipSection}>
        <Box className={classes.tooltipSectionTitle}>Customer charged</Box>
        <Box className={classes.tooltipLine}>
          <span>{foodTotalLabel}</span>
          <span>{formatCurrencyValue(foodTotal)}</span>
        </Box>
        {(row.salesTax ?? 0) !== 0 && (
          <Box className={classes.tooltipLine}>
            <span>Sales tax</span>
            <span>{formatCurrencyValue(row.salesTax)}</span>
          </Box>
        )}
        {Math.abs(tipsAndFees) > 0.005 && (
          <Box className={classes.tooltipLine}>
            <span>Tips & fees</span>
            <span>{formatCurrencyValue(tipsAndFees)}</span>
          </Box>
        )}
        <Box className={`${classes.tooltipLine} ${classes.tooltipTotal}`}>
          <span>= Total charged</span>
          <span>{formatCurrencyValue(row.transaction)}</span>
        </Box>
      </Box>

      {/* BOTTOM: How the deposit is calculated */}
      <Box className={classes.tooltipSection}>
        <Box className={classes.tooltipSectionTitle}>How your deposit is calculated</Box>

        {/* Pass-throughs: Stripe bundles these under their "Fee" label but
            they are remitted to third parties, not retained by Eatzy. */}
        {passThroughs.length > 0 && (
          <Box className={classes.tooltipPassThroughGroup}>
            <Box className={classes.tooltipSubLabel}>
              Pass-throughs — not Eatzy fees:
            </Box>
            {passThroughs.map(([label, value]) => (
              <Box key={label} className={`${classes.tooltipLine} ${classes.tooltipPassThrough}`}>
                <span>{label}</span>
                <span>{formatCurrencyValue(value)}</span>
              </Box>
            ))}
          </Box>
        )}

        {/* Restaurant's reconciliation equation: Menu − Eatzy − Stripe = Deposit */}
        <Box className={classes.tooltipPassThroughGroup}>
          <Box className={classes.tooltipSubLabel}>Eatzy &amp; Stripe costs:</Box>
          <Box className={`${classes.tooltipLine} ${classes.tooltipMenuPriceLine}`}>
            <span>Menu Price</span>
            <span>{formatCurrencyValue(row.menuPrice)}</span>
          </Box>
          <Box className={classes.tooltipLine}>
            <span>− Eatzy amount</span>
            <span>{formatCurrencyValue(row.eatzyAmount)}</span>
          </Box>
          <Box className={classes.tooltipLine}>
            <span>− Stripe processing fee</span>
            <span>{formatCurrencyValue(row.stripeFee)}</span>
          </Box>
        </Box>
      </Box>

      {/* When the order has a refund, show the deduction before the deposit
          total so the bottom section balances: Menu − Eatzy − Stripe − Refund
          = Deposit.  The refund is shown in amber to signal it reduces the
          deposit without implying an Eatzy or Stripe error. */}
      {(row.refundAmount ?? 0) > 0 && (
        <Box className={`${classes.tooltipLine} ${classes.tooltipRefundLine}`}>
          <span>− Refund</span>
          <span data-testid="tooltip-refund-amount">
            {formatCurrencyValue(row.refundAmount)}
          </span>
        </Box>
      )}

      <Box className={`${classes.tooltipLine} ${classes.tooltipDeposit}`}>
        <span>= Deposit</span>
        <span>{formatCurrencyValue(row.adjustedDeposit ?? row.deposit)}</span>
      </Box>
    </Box>
  );
}

RowBreakdownTooltip.propTypes = {
  row: PropTypes.object.isRequired,
};

export function OrderRow({ row, isPending, timezone }) {
  const classes = useStyles();
  const isStripeSourced = row.settlementSource === 'stripe';

  let chip;
  if (isPending && isStripeSourced) {
    const expected = formatExpectedChipDate(row.availableOn);
    chip = (
      <Chip
        label={expected ? `Expected ${expected}` : 'Awaiting payout'}
        size="small"
        className={classes.awaitingChip}
      />
    );
  } else if (!isStripeSourced) {
    chip = (
      <Tooltip title={ESTIMATED_TOOLTIP} arrow placement="top">
        <Chip
          label="Estimated"
          size="small"
          className={classes.estimatedChip}
        />
      </Tooltip>
    );
  } else {
    chip = (
      <Chip
        icon={<CheckCircleIcon />}
        label="Stripe"
        size="small"
        className={classes.confirmedChip}
      />
    );
  }

  const depositCell = (
    <Tooltip
      title={<RowBreakdownTooltip row={row} />}
      arrow
      placement="top"
      classes={{ tooltip: classes.tooltipPaper }}
    >
      <Box
        className={`${classes.rowCell} ${classes.colDeposit} ${classes.hasDetails}`}
      >
        <span>{formatCurrencyValue(row.adjustedDeposit ?? row.deposit)}</span>
      </Box>
    </Tooltip>
  );

  const orderDateLabel = row.orderDate || row.date;

  return (
    <Box
      className={`${classes.orderRow} ${
        !isStripeSourced ? classes.orderRowEstimated : ''
      }`}
      data-testid="order-row"
      data-settlement-source={row.settlementSource}
    >
      <Typography className={`${classes.rowCell} ${classes.colDateTime}`}>
        {orderDateLabel}
      </Typography>
      <Typography className={`${classes.rowCell} ${classes.colType}`}>
        {row.type}
      </Typography>
      <Typography className={`${classes.rowCell} ${classes.colId}`}>
        {row.id}
      </Typography>
      <Typography className={`${classes.rowCell} ${classes.colMenuPrice}`}>
        {formatCurrencyValue(row.menuPrice)}
      </Typography>
      <Typography className={`${classes.rowCell} ${classes.colEatzyAmount}`}>
        {formatCurrencyValue(row.eatzyAmount)}
      </Typography>
      <Typography className={`${classes.rowCell} ${classes.colEffectivePct}`}>
        {formatPercent(row.effectivePct, { menuPrice: row.menuPrice })}
      </Typography>
      <Box
        className={`${classes.rowCell} ${classes.colStripeFee} ${classes.feeCellWithIndicator}`}
      >
        {isFeeUnreconciled(row.stripeFee) && <UnreconciledFeeIndicator />}
        <span>{formatCurrencyValue(row.stripeFee)}</span>
      </Box>
      {depositCell}
      <Box className={classes.colStatus}>{chip}</Box>
    </Box>
  );
}

OrderRow.propTypes = {
  row: PropTypes.object.isRequired,
  isPending: PropTypes.bool,
  timezone: PropTypes.string,
};

OrderRow.defaultProps = {
  isPending: false,
};

export function GroupSubtotal({
  subtotalMenuPrice,
  subtotalEatzyAmount,
  subtotalEffectivePct,
  subtotalStripeFee,
  subtotalDeposit,
  stripePayoutAmount,
  deltaVsSummedDeposit,
  showReconciliation,
  onToggle,
}) {
  const classes = useStyles();
  const status = getReconciliationStatus({
    stripePayoutAmount,
    deltaVsSummedDeposit,
  });
  return (
    <Box
      className={classes.subtotalRow}
      data-testid="group-subtotal"
      onClick={onToggle}
      role={onToggle ? 'button' : undefined}
      tabIndex={onToggle ? 0 : undefined}
      onKeyDown={onToggle ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); }
      } : undefined}
      sx={onToggle ? { cursor: 'pointer', userSelect: 'none' } : undefined}
    >
      <Typography className={`${classes.subtotalLabel} ${classes.colDateTime}`}>
        GROUP TOTAL
      </Typography>
      <Typography className={`${classes.subtotalCell} ${classes.colType}`} />
      <Typography className={`${classes.subtotalCell} ${classes.colId}`} />
      <Typography
        className={`${classes.subtotalCell} ${classes.colMenuPrice}`}
      >
        {formatCurrencyValue(subtotalMenuPrice)}
      </Typography>
      <Typography
        className={`${classes.subtotalCell} ${classes.colEatzyAmount}`}
      >
        {formatCurrencyValue(subtotalEatzyAmount)}
      </Typography>
      <Typography
        className={`${classes.subtotalCell} ${classes.colEffectivePct}`}
      >
        {formatPercent(subtotalEffectivePct, { menuPrice: subtotalMenuPrice })}
      </Typography>
      <Box
        className={`${classes.subtotalCell} ${classes.colStripeFee} ${classes.feeCellWithIndicator}`}
      >
        {isFeeUnreconciled(subtotalStripeFee) && <UnreconciledFeeIndicator />}
        <span>{formatCurrencyValue(subtotalStripeFee)}</span>
      </Box>
      {showReconciliation ? (
        <Box className={`${classes.subtotalCell} ${classes.colDeposit} ${classes.depositSplitCell}`}>
          <Box className={classes.depositSplitLine}>
            <span className={classes.depositSplitLabel}>Our sum</span>
            <span data-testid="group-deposit-summed">
              {formatCurrencyValue(subtotalDeposit)}
            </span>
          </Box>
          <Box className={classes.depositSplitLine}>
            <span className={classes.depositSplitLabel}>Stripe payout</span>
            {stripePayoutAmount != null ? (
              <span data-testid="group-deposit-stripe-payout">
                {formatCurrencyValue(stripePayoutAmount)}
              </span>
            ) : (
              <Tooltip title={AWAITING_STRIPE_TOOLTIP} arrow placement="top">
                <span
                  className={classes.depositSplitMissing}
                  data-testid="group-deposit-stripe-payout-missing"
                  aria-label="Awaiting Stripe payout amount"
                >
                  —
                </span>
              </Tooltip>
            )}
          </Box>
        </Box>
      ) : (
        <Typography
          className={`${classes.subtotalCell} ${classes.colDeposit}`}
        >
          {formatCurrencyValue(subtotalDeposit)}
        </Typography>
      )}
      <Box className={classes.colStatus}>
        {showReconciliation && (
          <DepositReconciliationBadge
            status={status}
            summedDeposit={subtotalDeposit}
            stripePayoutAmount={stripePayoutAmount}
            deltaVsSummedDeposit={deltaVsSummedDeposit}
          />
        )}
      </Box>
    </Box>
  );
}

GroupSubtotal.propTypes = {
  subtotalMenuPrice: PropTypes.number,
  subtotalEatzyAmount: PropTypes.number,
  subtotalEffectivePct: PropTypes.number,
  subtotalStripeFee: PropTypes.number,
  subtotalDeposit: PropTypes.number,
  stripePayoutAmount: PropTypes.number,
  deltaVsSummedDeposit: PropTypes.number,
  showReconciliation: PropTypes.bool,
  onToggle: PropTypes.func,
};

GroupSubtotal.defaultProps = {
  stripePayoutAmount: null,
  deltaVsSummedDeposit: null,
  showReconciliation: false,
  onToggle: null,
};

export function PayoutGroup({ group, filteredRows }) {
  const classes = useStyles();
  const [isExpanded, setIsExpanded] = useState(false);
  const toggle = () => setIsExpanded((v) => !v);

  const arrivalDate = formatArrivalDate(group.settlementArrivalDate);
  const groupHeader = arrivalDate
    ? `Stripe deposit · ${arrivalDate}`
    : 'Stripe deposit';

  const hasRows = group.rows && group.rows.length > 0;
  const isSplit =
    hasRows && group.rows.length < (group.totalOrders ?? group.rows.length);

  const orderCount = group.totalOrders ?? group.rows?.length ?? 0;
  const orderLabel = orderCount === 1 ? '1 order' : `${orderCount} orders`;

  const searchActive = filteredRows !== null;
  const effectiveExpanded = searchActive ? filteredRows.length > 0 : isExpanded;
  const rowsToShow = searchActive ? filteredRows : group.rows;
  const showAnnotation =
    searchActive &&
    filteredRows.length > 0 &&
    filteredRows.length < group.rows.length;

  return (
    <Box className={classes.payoutGroup} data-testid="payout-group">
      <Box
        className={classes.payoutHeaderRow}
        onClick={toggle}
        data-testid="payout-group-header"
      >
        <Typography className={classes.payoutHeader}>
          {groupHeader}
          {orderCount > 0 && (
            <Box
              component="span"
              className={classes.orderCount}
              data-testid="payout-group-order-count"
            >
              {` · ${orderLabel}`}
            </Box>
          )}
        </Typography>
        <ExpandMoreIcon
          className={`${classes.chevron} ${effectiveExpanded ? classes.chevronExpanded : ''}`}
        />
      </Box>
      {isSplit && (
        <Typography
          className={classes.detailRowsElsewhere}
          data-testid="split-page-note"
          sx={{ marginBottom: '6px' }}
        >
          {`Showing ${group.rows.length} of ${group.totalOrders} orders · full deposit total below`}
        </Typography>
      )}
      <Box className={classes.scrollWrapper}>
        <Box className={classes.wrapperDay}>
          {effectiveExpanded && (
            <>
              <ColumnHeaders titleStyle={classes.titleWrapperMobile} />
              {hasRows ? (
                rowsToShow.map((row) => <OrderRow key={row.id} row={row} />)
              ) : (
                <Typography
                  className={classes.detailRowsElsewhere}
                  data-testid="payout-rows-on-other-page"
                >
                  {group.totalOrders
                    ? `${group.totalOrders} orders in this deposit — paginate to see them.`
                    : 'Detail rows on another page — paginate to see them.'}
                </Typography>
              )}
              {showAnnotation && (
                <Typography
                  className={classes.searchAnnotation}
                  data-testid="search-group-annotation"
                >
                  {`(showing ${filteredRows.length} of ${group.rows.length} orders)`}
                </Typography>
              )}
            </>
          )}
          <GroupSubtotal
            subtotalMenuPrice={group.subtotal_menu_price}
            subtotalEatzyAmount={group.subtotal_eatzy_amount}
            subtotalEffectivePct={group.subtotal_effective_pct}
            subtotalStripeFee={group.subtotal_stripe_fee}
            subtotalDeposit={group.subtotal_deposit}
            stripePayoutAmount={group.stripePayoutAmount}
            deltaVsSummedDeposit={group.deltaVsSummedDeposit}
            showReconciliation
            onToggle={toggle}
          />
        </Box>
      </Box>
    </Box>
  );
}

PayoutGroup.propTypes = {
  group: PropTypes.object.isRequired,
  filteredRows: PropTypes.array,
};

PayoutGroup.defaultProps = {
  filteredRows: null,
};

export function PendingDateGroup({ group, timezone }) {
  const classes = useStyles();
  const headerDate = formatExpectedHeaderDate(group.availableOn);
  const isUnknownDate = group.availableOn == null;
  const orderCount = group.rows.length;
  const orderWord = orderCount === 1 ? 'order' : 'orders';

  return (
    <Box className={classes.payoutGroup} data-testid="pending-date-group">
      <>
        <Typography className={classes.payoutHeader}>
          {isUnknownDate
            ? `Date not yet provided by Stripe — ${orderCount} ${orderWord}`
            : `Expected deposit · ${headerDate} — ${orderCount} ${orderWord}`}
        </Typography>
        {isUnknownDate && (
          <Typography
            className={classes.pendingSubtitle}
            data-testid="pending-unknown-date-explanation"
          >
            Stripe hasn&apos;t quoted an arrival date for these orders yet.
            They&apos;ll move into a dated group once Stripe confirms when the
            funds will land.
          </Typography>
        )}
      </>
      <Box className={classes.scrollWrapper}>
        <ColumnHeaders titleStyle={classes.titleWrapperMobile} />
        <Box className={classes.wrapperDay}>
          {group.rows.map((row) => (
            <OrderRow key={row.id} row={row} isPending timezone={timezone} />
          ))}
          <GroupSubtotal
            subtotalMenuPrice={group.subtotal_menu_price}
            subtotalEatzyAmount={group.subtotal_eatzy_amount}
            subtotalEffectivePct={group.subtotal_effective_pct}
            subtotalStripeFee={group.subtotal_stripe_fee}
            subtotalDeposit={group.subtotal_deposit}
          />
        </Box>
      </Box>
    </Box>
  );
}

PendingDateGroup.propTypes = {
  group: PropTypes.object.isRequired,
  timezone: PropTypes.string,
};

export function PendingPayoutsSection({
  groups,
  totalPending,
  truncated,
  timezone,
}) {
  const classes = useStyles();
  if (!totalPending) return null;

  return (
    <Box data-testid="pending-section">
      {truncated && (
        <Box
          className={classes.pendingTruncatedNotice}
          data-testid="pending-truncated-notice"
        >
          Showing the 1000 most recent pending orders — narrow your date
          range to see them all.
        </Box>
      )}
      <Box className={classes.pendingGroup}>
        <Box className={classes.pendingHeaderWrap}>
          <Typography className={classes.pendingHeader}>
            Pending payouts — {totalPending}{' '}
            {totalPending === 1 ? 'order' : 'orders'}
          </Typography>
          <Typography className={classes.pendingSubtitle}>
            These orders haven&apos;t reached your bank yet, so the totals
            above don&apos;t include them.
          </Typography>
        </Box>
        {groups.map((group, idx) => (
          <PendingDateGroup
            key={group.availableOn ?? `unknown-${idx}`}
            group={group}
            timezone={timezone}
          />
        ))}
      </Box>
    </Box>
  );
}

PendingPayoutsSection.propTypes = {
  groups: PropTypes.array,
  totalPending: PropTypes.number,
  truncated: PropTypes.bool,
  timezone: PropTypes.string,
};

PendingPayoutsSection.defaultProps = {
  groups: [],
  totalPending: 0,
  truncated: false,
};

export function DepositReconciliation() {
  const commonClasses = useCommonStyles();
  const classes = useStyles();
  const { selectedRestaurant } = useMenuContext();
  const [data, setData] = useState(null);
  const [modeUsed, setModeUsed] = useState(null);
  const [orderSearch, setOrderSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [reloading, setReloading] = useState(false);
  const [error, setError] = useState(false);
  const [errorRequestId, setErrorRequestId] = useState(null);
  const [errorStatus, setErrorStatus] = useState(null);
  const [filter, setFilter] = useState({ label: 'Last 7 days' });
  const filterBtnEl = useRef();
  const [popoverEl, setPopoverEl] = useState(null);
  const [params, setParams] = useState({});
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  // The search term currently in effect on the server (empty = unfiltered data).
  // Set after each successful fetch so pagination and clear-search can replay it.
  const [serverSearchQ, setServerSearchQ] = useState('');
  // True while a server-side search request is in-flight (distinct from the
  // general `reloading` flag so only the search-note area shows the spinner).
  const [searchLoading, setSearchLoading] = useState(false);
  // Set to true when a server-side search request fails. The previously-loaded
  // unfiltered data remains on screen; only an inline note is shown near the
  // search bar. Cleared at the start of any subsequent fetchData call.
  const [searchError, setSearchError] = useState(false);
  // True while a cached page is showing and a background revalidation is in
  // flight. Shows a small "Refreshing…" note so partners know the data is
  // live, not frozen.
  const [revalidating, setRevalidating] = useState(false);
  // Monotonic id of the most recently issued request. Older in-flight
  // requests check this on resolution and bail out, so a stale full-mode
  // response from a previous filter/restaurant cannot overwrite a newer
  // fallback-mode response (or vice versa) and mis-render the banner.
  const requestSeqRef = useRef(0);
  // AbortController for the most recently issued request. When a new
  // fetch is kicked off (filter/restaurant change, pagination, retry),
  // the prior in-flight request is aborted so it stops consuming a
  // gateway connection in the background — the requestSeqRef guard
  // alone only drops the result, it does NOT cancel the underlying
  // fetch. On slow links / rapid filter switches this is the difference
  // between a single in-flight request and a pile-up.
  const abortRef = useRef(null);
  // Mirror of `modeUsed` consumed by fetchData's catch path so we can
  // attach `fallback_used` Bugsnag metadata without putting `modeUsed` in
  // fetchData's useCallback deps — adding it would re-create fetchData on
  // every successful fetch and cause the [fetchData]-bound useEffect to
  // re-fire, kicking off an infinite re-fetch loop.
  const modeUsedRef = useRef(null);
  useEffect(() => {
    modeUsedRef.current = modeUsed;
  }, [modeUsed]);
  // Tracks whether the arrival-date endpoint has already failed once in this
  // session. When true, subsequent fetches pass `forceFallback: true` to the
  // service so they skip the doomed first attempt and go straight to the
  // transaction-date endpoint — halving latency while the backend is degraded.
  // Reset to false when the user clicks "Try again" (so a full retry is
  // possible) or when the restaurant/date context changes.
  const sessionFallbackRef = useRef(false);

  // ── Page cache ────────────────────────────────────────────────────────────
  // In-memory cache for already-fetched pages within the current date window
  // and restaurant. Key: `${restaurantId}|${startMs}|${endMs}|${pageNum}|${q}`.
  // Cleared automatically when restaurant or date range changes.
  const pageCacheRef = useRef({});
  // AbortController for in-flight background prefetch requests (separate from
  // abortRef so user-initiated fetches never accidentally abort prefetches that
  // are about to fill the next page slot).
  const prefetchAbortRef = useRef(null);
  // Last-seen cache-context key (restaurantId + date range). Used to detect
  // context switches so the cache is flushed before the new data arrives.
  const cacheContextRef = useRef(null);
  // Frozen SummaryBar totals — captured on the first successful window-scoped
  // fetch for each date/restaurant context; held stable while the user pages
  // through results so the bar never flickers to zero mid-pagination.
  // Reset to null when the context key changes (restaurant or date range switch)
  // so a new window's first fetch re-captures the correct totals.
  const frozenSummaryRef = useRef(null);

  const fetchData = useCallback(
    // `q`         — optional server-side search term (empty = unfiltered).
    // `skipCache` — bypass the page cache for user-initiated retries so the
    //               request always hits the network (e.g. "Try again" button).
    async (pageNum, { q, skipCache } = {}) => {
      if (!selectedRestaurant?.id || !params.start) return;

      // Cancel the previous user-initiated request. This stops the underlying
      // fetch rather than just dropping the resolved value (see abortRef comment).
      if (abortRef.current) abortRef.current.abort();
      // Also cancel any pending prefetch — a new user-initiated fetch takes
      // priority and the prefetch result would be stale after a context switch.
      if (prefetchAbortRef.current) {
        prefetchAbortRef.current.abort();
        prefetchAbortRef.current = null;
      }

      const controller =
        typeof AbortController !== 'undefined' ? new AbortController() : null;
      abortRef.current = controller;
      const seq = requestSeqRef.current + 1;
      requestSeqRef.current = seq;
      setError(false);
      setErrorRequestId(null);
      setErrorStatus(null);
      setSearchError(false);
      if (q) setSearchLoading(true);

      // ── Cache context ───────────────────────────────────────────────────
      // Changing restaurant or date range invalidates all cached pages.
      // We detect this by comparing a "context key" on every call.
      const startMs =
        params.start instanceof Date
          ? params.start.getTime()
          : Number(params.start);
      const endMs =
        params.end instanceof Date
          ? params.end.getTime()
          : Number(params.end);
      const cacheContext = `${selectedRestaurant.id}|${startMs}|${endMs}`;
      if (cacheContextRef.current !== cacheContext) {
        pageCacheRef.current = {};
        cacheContextRef.current = cacheContext;
        // Thaw: a new date/restaurant context means the frozen totals are
        // stale. The first successful fetch for this context will re-freeze.
        frozenSummaryRef.current = null;
        // New restaurant or date range: start fresh — don't carry over the
        // fallback assumption from the previous context.
        sessionFallbackRef.current = false;
      }

      // ── Adjacent-page prefetch helper ─────────────────────────────────
      // Shared by both the uncached-fetch and cache-hit-revalidation paths
      // so that every successful page load warms pages N+1 and N-1.
      const firePrefetchAround = (totalPages) => {
        const prefetchController =
          typeof AbortController !== 'undefined'
            ? new AbortController()
            : null;
        prefetchAbortRef.current = prefetchController;

        const prefetchPage = (targetPage) => {
          if (targetPage < 1 || targetPage > totalPages) return;
          const targetKey = `${cacheContext}|${targetPage}|${q || ''}`;
          if (pageCacheRef.current[targetKey]) return;
          getStripeReconciliationByPayout(selectedRestaurant.id, {
            ...params,
            page: targetPage,
            signal: prefetchController?.signal,
            forceFallback: sessionFallbackRef.current,
            ...(q ? { q } : {}),
          })
            .then(({ data: pData, modeUsed: pMode }) => {
              if (prefetchController?.signal?.aborted) return;
              // Only write to cache when the context hasn't changed.
              if (cacheContextRef.current === cacheContext) {
                pageCacheRef.current[targetKey] = {
                  data: pData,
                  modeUsed: pMode,
                };
              }
            })
            .catch(() => {
              // Prefetch failures are silently ignored — they don't affect
              // the currently visible page in any way.
            });
        };

        prefetchPage(pageNum + 1);
        prefetchPage(pageNum - 1);
      };

      // ── Cache look-up ───────────────────────────────────────────────────
      const cacheKey = `${cacheContext}|${pageNum}|${q || ''}`;
      const cached = skipCache ? null : pageCacheRef.current[cacheKey];

      if (cached) {
        // Serve the cached page instantly — no loading/reloading spinner.
        if (requestSeqRef.current !== seq) return;
        setTotalPages(cached.data.total_pages);
        setPage(Number(cached.data.page));
        setData(cached.data);
        setModeUsed(cached.modeUsed);
        setServerSearchQ(q || '');
        setSearchLoading(false);
        setLoading(false);
        setReloading(false);
        setRevalidating(true);

        // Background revalidation: keep the cache warm and pick up any
        // backend changes without blocking the user.
        try {
          const { data: fresh, modeUsed: freshMode } =
            await getStripeReconciliationByPayout(selectedRestaurant.id, {
              ...params,
              page: pageNum,
              signal: controller?.signal,
              forceFallback: sessionFallbackRef.current,
              ...(q ? { q } : {}),
            });
          if (requestSeqRef.current !== seq) { setRevalidating(false); return; }
          if (freshMode === STRIPE_RECON_MODE_FALLBACK) sessionFallbackRef.current = true;
          pageCacheRef.current[cacheKey] = { data: fresh, modeUsed: freshMode };
          setData(fresh);
          setModeUsed(freshMode);
          setTotalPages(fresh.total_pages);
          setPage(Number(fresh.page));
          setServerSearchQ(q || '');
          setRevalidating(false);
          // Also prefetch adjacent pages after a revalidation so navigating
          // from a cached page stays warm in both directions.
          firePrefetchAround(fresh.total_pages);
        } catch (revalErr) {
          if (requestSeqRef.current !== seq) { setRevalidating(false); return; }
          if (revalErr?.name === 'AbortError' || controller?.signal?.aborted) {
            setRevalidating(false);
            return;
          }
          // Revalidation failed — keep showing the cached page; the user
          // already has useful data on screen. Hide the indicator.
          setRevalidating(false);
        }
        return;
      }

      // ── Normal (uncached) fetch ─────────────────────────────────────────
      setReloading(true);
      setRevalidating(false);
      try {
        const { data: response, modeUsed: nextMode } =
          await getStripeReconciliationByPayout(selectedRestaurant.id, {
            ...params,
            page: pageNum,
            signal: controller?.signal,
            forceFallback: sessionFallbackRef.current,
            ...(q ? { q } : {}),
          });
        // Discard stale responses — only the latest issued request is
        // allowed to flip page state.
        if (requestSeqRef.current !== seq) return;

        // Record a fallback so subsequent fetches (pagination, prefetch,
        // revalidation) skip the doomed arrival-date attempt.
        if (nextMode === STRIPE_RECON_MODE_FALLBACK) sessionFallbackRef.current = true;

        // Store the successful response in the page cache.
        pageCacheRef.current[cacheKey] = { data: response, modeUsed: nextMode };

        // ── Freeze SummaryBar totals ──────────────────────────────────────
        // Must be set BEFORE the state-setter calls below. In React 16,
        // each setState in an async callback can trigger a synchronous
        // re-render before the next line runs; if we set the ref after
        // the setters, the first re-render sees a null ref and the
        // SummaryBar never appears.
        //
        // Capture window-level totals on the first successful non-search
        // load for this date/restaurant context. Subsequent page fetches
        // leave the ref untouched so the SummaryBar never flickers to zero
        // mid-pagination. Search fetches are excluded because their
        // envelope totals reflect the filtered subset, not the full window.
        //
        // Exception: explicit user-initiated retries (skipCache=true, e.g.
        // the "Try again" button) MUST be allowed to re-freeze totals —
        // the retry may switch from fallback to full mode, changing the
        // envelope totals, and the user explicitly asked for fresh data.
        if (!q && (!frozenSummaryRef.current || skipCache)) {
          const wGroups = response.windowGroups || [];
          const spAvailable =
            wGroups.length > 0 &&
            wGroups.every((g) => g.stripePayoutAmount != null);
          frozenSummaryRef.current = {
            totalMenuPrice: response.total_menu_price,
            totalEatzyAmount: response.total_eatzy_amount,
            totalEffectivePct: response.total_effective_pct,
            totalStripeFee: response.total_stripe_fee,
            totalDeposit: response.total_adjusted_deposit ?? response.total_deposit,
            stripePayoutTotal: spAvailable
              ? wGroups.reduce((acc, g) => acc + (g.stripePayoutAmount || 0), 0)
              : 0,
            stripePayoutTotalAvailable: spAvailable,
          };
        }

        setTotalPages(response.total_pages);
        setPage(Number(response.page));
        setData(response);
        setModeUsed(nextMode);
        // Record which search term is in effect; '' means full unfiltered set.
        setServerSearchQ(q || '');
        setSearchLoading(false);
        setLoading(false);
        setReloading(false);

        // Warm pages N+1 and N-1 so the user can navigate either direction
        // without a full round-trip.
        firePrefetchAround(response.total_pages);
      } catch (err) {
        // Aborted by a newer fetch — silently drop.
        if (err?.name === 'AbortError' || controller?.signal?.aborted) return;
        if (requestSeqRef.current !== seq) return;
        const reqId =
          err?.requestId || err?.__apiContext?.requestId || null;
        // When the previous successful render came from the fallback path,
        // carry that context into Bugsnag so an on-call can correlate.
        notifyError(err, {
          component: 'StripeReconciliation',
          method: 'fetchData',
          ...(modeUsedRef.current === STRIPE_RECON_MODE_FALLBACK && {
            fallback_used: 'transaction-date',
          }),
          ...(reqId && { request_id: reqId }),
          ...(q && { search_query: q }),
        });
        if (q) {
          // Search failure: keep the previously-loaded unfiltered data on
          // screen; show only an inline note.
          setSearchError(true);
          setSearchLoading(false);
          setReloading(false);
        } else {
          setErrorRequestId(reqId);
          setErrorStatus(err?.status ?? null);
          setError(true);
          setSearchLoading(false);
          setLoading(false);
          setReloading(false);
        }
      }
    },
    [params, selectedRestaurant]
  );

  // Keep a stable ref to fetchData so the debounce effect below can call the
  // latest version without listing fetchData in its own deps — if fetchData
  // were in those deps, every params/restaurant change would restart the
  // debounce timer and potentially fire a spurious server search.
  const fetchDataRef = useRef(fetchData);
  useEffect(() => {
    fetchDataRef.current = fetchData;
  }, [fetchData]);

  useEffect(() => {
    const presets = {
      'Last 7 days': last7Days,
      'Last week': lastWeek,
      'Last 30 days': last30Days,
      'Last month': lastMonth,
      'Last 3 months': last3Months,
    };
    const preset = presets[filter.label];
    if (preset) {
      setParams(preset());
    }
    // Clear the search box whenever the date range changes so the visible
    // input always matches what was actually sent to the server. Also reset
    // serverSearchQ so the search useEffect (which reacts to orderSearch
    // becoming '') does not fire a spurious reload on top of the normal
    // filter-change fetch.
    setOrderSearch('');
    setServerSearchQ('');
  }, [filter]);

  useEffect(() => {
    fetchData(1);
  }, [fetchData]);

  // Server-side cross-page search (Task #154).
  // Fires a debounced API call whenever the search term changes AND the current
  // result set spans more than one page (or we're already in server-search
  // mode, where the backend has already filtered the data).
  // Single-page result sets skip this path entirely — client-side filtering
  // (Task #150) handles them instantly without an extra round-trip.
  useEffect(() => {
    if (!orderSearch) {
      // User cleared the search box while server-filtered data was showing →
      // reload the full unfiltered result set.
      if (serverSearchQ) {
        fetchDataRef.current(1, { q: '' });
      }
      return;
    }
    // Fast path: single-page result with no active server search — client-side
    // filtering (Task #150) is sufficient, no API call needed.
    if (totalPages <= 1 && !serverSearchQ) return;

    const timer = setTimeout(() => {
      fetchDataRef.current(1, { q: orderSearch.trim() });
    }, 400);
    return () => clearTimeout(timer);
  }, [orderSearch, totalPages, serverSearchQ]);

  // On unmount, abort any still-in-flight requests so they don't keep
  // gateway connections open after the user navigates away.
  useEffect(
    () => () => {
      if (abortRef.current) abortRef.current.abort();
      if (prefetchAbortRef.current) prefetchAbortRef.current.abort();
    },
    []
  );

  const handleChangePage = useCallback(
    (_, newPage) => {
      // Propagate the active server-search term to the next page so the
      // backend keeps filtering across the full result set.
      fetchData(newPage, serverSearchQ ? { q: serverSearchQ } : {});
    },
    [fetchData, serverSearchQ]
  );

  const handlePopoverOpen = () => setPopoverEl(filterBtnEl.current);
  const handlePopoverClose = () => setPopoverEl(null);

  if (loading) return <Loading />;
  if (error) {
    if (errorStatus === 403) {
      return (
        <ReportUnavailable
          sessionExpired
          onRetry={() => fetchData(page, { skipCache: true })}
        />
      );
    }
    return <ReportUnavailable requestId={errorRequestId} />;
  }

  const hasSettled = data?.formattedResults?.length > 0;
  const totalPending = data?.total_pending || 0;
  const hasPending = totalPending > 0;
  const hasAnyData = hasSettled || hasPending;

  return (
    <>
      <Box flex="1">
        <Box className={commonClasses.header}>
          <Typography variant="h1" className={commonClasses.pageTitle}>
            Deposit Reconciliation
          </Typography>
          <Box className={classes.filterRow}>
            {selectedRestaurant?.timezone && (
              <Typography
                className={classes.timezoneLabel}
                data-testid="timezone-label"
              >
                Times in {selectedRestaurant.timezone}
              </Typography>
            )}
            <Box data-testid="filter" ref={filterBtnEl}>
              <DropdownFilter
                items={{
                  filters: [
                    'Last 7 days',
                    'Last week',
                    'Last 30 days',
                    'Last month',
                    'Last 3 months',
                    {
                      label: 'Custom',
                      open: handlePopoverOpen,
                      close: handlePopoverClose,
                    },
                  ],
                  filter,
                  setFilter,
                }}
                buttonWidth="147px"
              />
            </Box>
          </Box>
        </Box>

        {modeUsed === STRIPE_RECON_MODE_FALLBACK && (
          <FallbackBanner
            onRetry={() => {
              // Allow the next attempt to try the full arrival-date endpoint
              // again — the user explicitly asked for a retry, so don't skip
              // ahead to the fallback.
              sessionFallbackRef.current = false;
              fetchData(page, { skipCache: true });
            }}
          />
        )}

        {revalidating && (
          <Box
            className={classes.revalidatingBanner}
            data-testid="revalidating-indicator"
            role="status"
            aria-live="polite"
          >
            <CircularProgress size={10} thickness={5} />
            <Typography component="span" className={classes.revalidatingText}>
              Refreshing…
            </Typography>
          </Box>
        )}

        {hasSettled && frozenSummaryRef.current && (
          // SummaryBar reads from the frozen ref so values are stable while
          // the user pages through results. The ref is captured on first
          // successful load and reset only when the restaurant or date range
          // changes (see frozenSummaryRef above). This prevents the bar from
          // flickering to zero during page transitions.
          <SummaryBar
            totalMenuPrice={frozenSummaryRef.current.totalMenuPrice}
            totalEatzyAmount={frozenSummaryRef.current.totalEatzyAmount}
            totalEffectivePct={frozenSummaryRef.current.totalEffectivePct}
            totalStripeFee={frozenSummaryRef.current.totalStripeFee}
            totalDeposit={frozenSummaryRef.current.totalDeposit}
            stripePayoutTotal={frozenSummaryRef.current.stripePayoutTotal}
            stripePayoutTotalAvailable={frozenSummaryRef.current.stripePayoutTotalAvailable}
          />
        )}

        <Box display="flex" flexDirection="column">
          {hasAnyData ? (
            <>
              <Box className={classes.searchBar} data-testid="order-search-bar">
                <InputBase
                  className={classes.searchInput}
                  placeholder="Find an order..."
                  value={orderSearch}
                  onChange={(e) => setOrderSearch(e.target.value)}
                  inputProps={{ 'data-testid': 'order-search-input', 'aria-label': 'Find an order' }}
                />
                {orderSearch && (
                  <IconButton
                    size="small"
                    onClick={() => setOrderSearch('')}
                    aria-label="Clear search"
                    data-testid="order-search-clear"
                  >
                    <CloseIcon style={{ fontSize: 16 }} />
                  </IconButton>
                )}
              </Box>
              {orderSearch && (
                searchLoading ? (
                  <Box
                    className={classes.searchLoadingIndicator}
                    data-testid="search-loading-indicator"
                  >
                    <CircularProgress size={12} thickness={5} />
                    <Typography component="span" className={classes.searchPageNote} style={{ marginBottom: 0 }}>
                      Searching all pages…
                    </Typography>
                  </Box>
                ) : searchError ? (
                  <Typography
                    className={classes.searchErrorNote}
                    data-testid="search-error-note"
                    role="alert"
                  >
                    Search failed — showing all orders instead
                  </Typography>
                ) : (
                  totalPages > 1 && !serverSearchQ && (
                    <Typography
                      className={classes.searchPageNote}
                      data-testid="search-page-note"
                    >
                      Search covers this page only — paginate to search other pages
                    </Typography>
                  )
                )
              )}

              <ColumnHeaders titleStyle={classes.titleWrapperDesktop} />

              <Box className={classes.infoWrapper}>
                {reloading ? (
                  <Loading />
                ) : (
                  <Box className={classes.groupWrapper}>
                    {data.formattedResults.map((group, idx) => {
                      const q = orderSearch.trim().toLowerCase();
                      const filteredRows = q
                        ? (group.rows || []).filter(
                            (row) =>
                              String(row.id || '').toLowerCase().includes(q) ||
                              String(row.orderDate || '').toLowerCase().includes(q)
                          )
                        : null;
                      return (
                        <PayoutGroup
                          key={
                            group.payoutId ||
                            `arrival-${group.settlementArrivalDate || idx}`
                          }
                          group={group}
                          filteredRows={filteredRows}
                        />
                      );
                    })}
                    {hasPending && (
                      <PendingPayoutsSection
                        groups={data.pendingGroups || []}
                        totalPending={totalPending}
                        truncated={!!data.pending_truncated}
                        timezone={selectedRestaurant?.timezone}
                      />
                    )}
                  </Box>
                )}
              </Box>
            </>
          ) : (
            <Box className={classes.noRecords}>No records found</Box>
          )}
        </Box>

        {totalPages > 1 && (
          <Pagination
            page={page}
            count={totalPages}
            size="small"
            onChange={handleChangePage}
            style={{ margin: '15px auto' }}
          />
        )}
      </Box>
      <DatePicker
        popoverEl={popoverEl}
        handleClosePopover={handlePopoverClose}
        setParams={setParams}
      />
    </>
  );
}

export default DepositReconciliation;
